import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCalendar,
  faDollarSign,
  faArrowUp,
  faArrowDown,
  faMinus,
  faDownload,
  faFileInvoice,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import { useAIAnalysis, AIInsightsPanel, AIInsightsButton } from './AIInsightsPanel';
import { costService } from '../services/costService';

interface MonthlyTrendsTabProps {
  data: any;
  loading?: boolean;
}

interface MonthlySpend {
  month: string;   // YYYY-MM-01
  label: string;   // "Jan '24"
  year: number;
  monthName: string; // "January"
  aws: number;
  gcp: number;
  github: number;
  custom: number;
  total: number;
  billsUploaded: number;
  change: number;
  changePercent: number;
}

// ─── helpers ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Transform /api/cost/analysis/twelve-months response into MonthlySpend[].
 * The API returns months oldest→newest; we keep that order for the table
 * (caller can reverse for display).
 */
const transformApiData = (apiMonths: Array<{
  month: string;   // YYYY-MM-01
  label: string;
  aws: number;
  gcp: number;
  github: number;
  total: number;
}>): MonthlySpend[] => {
  return apiMonths.map((m, idx, arr) => {
    const date = new Date(m.month);
    const year = date.getFullYear();
    const monthName = MONTH_NAMES[date.getMonth()];

    const prev = idx > 0 ? arr[idx - 1] : null;
    const change = prev ? m.total - prev.total : 0;
    const changePercent = prev && prev.total > 0 ? (change / prev.total) * 100 : 0;

    return {
      month: m.month,
      label: m.label,
      year,
      monthName,
      aws: m.aws || 0,
      gcp: m.gcp || 0,
      github: m.github || 0,
      custom: 0,   // custom cost data comes from a separate API; 0 until wired
      total: m.total || 0,
      billsUploaded: 0, // bills data not part of twelve-months API yet
      change,
      changePercent,
    };
  });
};

// ─── icon / colour helpers ───────────────────────────────────────────────────

const getProviderIcon = (provider: string) => {
  switch (provider) {
    case 'aws': return faAws;
    case 'gcp': return faGoogle;
    case 'github': return faGithub;
    default: return faDollarSign;
  }
};

const getProviderColor = (provider: string) => {
  switch (provider) {
    case 'aws': return 'text-orange-600';
    case 'gcp': return 'text-blue-600';
    case 'github': return 'text-gray-600';
    default: return 'text-purple-600';
  }
};

const getTrendIcon = (change: number) => {
  if (change > 0) return faArrowUp;
  if (change < 0) return faArrowDown;
  return faMinus;
};

const getTrendColor = (change: number) => {
  if (change > 0) return 'text-red-600';
  if (change < 0) return 'text-green-600';
  return 'text-gray-600';
};

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── component ───────────────────────────────────────────────────────────────

export const MonthlyTrendsTab: React.FC<MonthlyTrendsTabProps> = ({ data, loading = false }) => {
  const [allData, setAllData] = useState<MonthlySpend[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Available years derived from API data (descending)
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Selected year — default to current year, falls back to first available
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // AI Analysis
  const { aiAnalysis, showAIInsights, analyzing, fetchAnalysis, setShowAIInsights } = useAIAnalysis('monthly-trends');

  // Track previous year so we can close the panel when it changes
  const prevYearRef = useRef<number>(selectedYear);

  // ── fetch real data ────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setApiLoading(true);
    setApiError(null);
    try {
      // Fetch twelve-month data, custom entries, and bills in parallel
      const [result, customEntries, billEntries] = await Promise.all([
        costService.getTwelveMonthAnalysis(),
        costService.getCustomEntries().catch(() => []),
        costService.getBills().catch(() => [])
      ]);

      // result.months → array oldest→newest
      const months: Array<{ month: string; label: string; aws: number; gcp: number; github: number; total: number }> =
        result?.months || [];

      const transformed = transformApiData(months);

      // Aggregate custom cost entries by month (YYYY-MM)
      const customByMonth: Record<string, number> = {};
      (customEntries || []).forEach((entry: any) => {
        if (entry.month) {
          const key = entry.month.substring(0, 7); // YYYY-MM
          customByMonth[key] = (customByMonth[key] || 0) + (entry.amount || 0);
        }
      });

      // Aggregate uploaded bills amount by month (YYYY-MM)
      // NOTE: Bill.toJSON rewrites `month` to a display string like "April 2026"
      // and exposes the raw YYYY-MM as `monthKey`. Use monthKey here, otherwise
      // every key becomes "April 2"-style garbage and nothing aggregates.
      const billsByMonth: Record<string, number> = {};
      (billEntries || []).forEach((bill: any) => {
        const key = bill.monthKey || (typeof bill.month === 'string' && /^\d{4}-\d{2}/.test(bill.month) ? bill.month.substring(0, 7) : null);
        if (key) {
          billsByMonth[key] = (billsByMonth[key] || 0) + (bill.amount || 0);
        }
      });

      // Merge custom costs and bills into monthly data and recalculate totals
      transformed.forEach(m => {
        const yearMonth = m.month.substring(0, 7);
        m.custom = customByMonth[yearMonth] || 0;
        m.billsUploaded = billsByMonth[yearMonth] || 0;
        m.total = m.aws + m.gcp + m.github + m.custom;
      });

      // Recalculate change & changePercent now that custom costs are included
      transformed.forEach((m, idx) => {
        if (idx > 0) {
          m.change = m.total - transformed[idx - 1].total;
          m.changePercent = transformed[idx - 1].total > 0
            ? (m.change / transformed[idx - 1].total) * 100
            : 0;
        }
      });

      setAllData(transformed);

      // Build unique year list descending — using filter+indexOf avoids Set iteration
      // (Set spread / Array.from(Set) can trigger TS2802 in older tsconfig targets)
      const years = transformed
        .map(m => m.year)
        .filter((y, i, arr) => arr.indexOf(y) === i)
        .sort((a, b) => b - a);
      setAvailableYears(years);

      // Pick selected year: prefer currentYear (constant), else the most recent one with data.
      // NOTE: we use currentYear (not selectedYear state) to avoid stale closure inside useCallback([]).
      if (years.length > 0 && !years.includes(currentYear)) {
        setSelectedYear(years[0]);
      }
    } catch (err: any) {
      console.error('MonthlyTrendsTab: failed to fetch data', err);
      setApiError(err?.message || 'Failed to load monthly cost data');
    } finally {
      setApiLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── close AI panel when year changes ──────────────────────────────────────
  useEffect(() => {
    if (prevYearRef.current !== selectedYear) {
      setShowAIInsights(false);
      prevYearRef.current = selectedYear;
    }
  }, [selectedYear, setShowAIInsights]);

  // ── filtered data for selected year (newest month first in table) ──────────
  const filteredData = allData
    .filter(item => item.year === selectedYear)
    .slice()
    .reverse(); // newest first

  // ── summary stats ──────────────────────────────────────────────────────────
  const totalSpend = filteredData.reduce((sum, item) => sum + item.total, 0);
  const avgSpend = filteredData.length > 0 ? totalSpend / filteredData.length : 0;
  const highestMonth = filteredData.length > 0
    ? filteredData.reduce((max, item) => item.total > max.total ? item : max, filteredData[0])
    : null;
  const lowestMonth = filteredData.length > 0
    ? filteredData.reduce((min, item) => item.total < min.total ? item : min, filteredData[0])
    : null;

  // ── AI insights handler ────────────────────────────────────────────────────
  const handleAIAnalysis = async () => {
    await fetchAnalysis({
      monthlyData: filteredData,
      selectedYear,
      totalSpend,
      avgSpend,
      highestMonth: highestMonth ? { month: highestMonth.monthName, total: highestMonth.total } : null,
      lowestMonth: lowestMonth ? { month: lowestMonth.monthName, total: lowestMonth.total } : null,
      summary: { totalSpend, avgSpend }
    });
  };

  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (filteredData.length === 0) return;
    const headers = ['Month', 'AWS', 'GCP', 'GitHub', 'Custom', 'Total', 'Change %'];
    const rows = filteredData.map(item => [
      `${item.monthName} ${item.year}`,
      item.aws.toFixed(2),
      item.gcp.toFixed(2),
      item.github.toFixed(2),
      item.custom.toFixed(2),
      item.total.toFixed(2),
      `${item.changePercent > 0 ? '+' : ''}${item.changePercent.toFixed(1)}%`
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly-trends-${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="text-blue-600 mr-2" />
              Monthly Cost Trends
            </h2>
            <p className="text-xs text-gray-600">
              Historical monthly spend analysis and cost trends across all providers
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Year Dropdown — dynamic from API data */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              disabled={apiLoading || availableYears.length === 0}
              className="px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              {availableYears.length > 0 ? (
                availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))
              ) : (
                // Fallback while loading
                <option value={currentYear}>{currentYear}</option>
              )}
            </select>

            {/* AI Insights — enabled whenever there's data for selected year */}
            <AIInsightsButton
              onClick={handleAIAnalysis}
              loading={analyzing}
              disabled={filteredData.length === 0 || apiLoading}
            />

            <button
              onClick={exportCSV}
              disabled={filteredData.length === 0 || apiLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* AI Insights Panel */}
      {showAIInsights && aiAnalysis && (
        <AIInsightsPanel
          analysis={aiAnalysis}
          onClose={() => setShowAIInsights(false)}
        />
      )}

      {/* Loading state */}
      {apiLoading && (
        <div className="bg-white border border-gray-300 rounded p-8 text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-3xl text-blue-500 mb-3 animate-spin" />
          <div className="text-xs text-gray-600">Loading monthly cost data…</div>
        </div>
      )}

      {/* Error state */}
      {!apiLoading && apiError && (
        <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start space-x-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5" />
          <div>
            <div className="text-xs font-medium text-red-700">Failed to load data</div>
            <div className="text-xs text-red-600 mt-1">{apiError}</div>
            <button
              onClick={fetchData}
              className="mt-2 text-xs text-red-700 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main content — only when data loaded */}
      {!apiLoading && !apiError && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Total Spend ({selectedYear})</div>
                  <div className="text-xs font-semibold text-gray-900">${fmt(totalSpend)}</div>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                  <FontAwesomeIcon icon={faDollarSign} className="text-blue-600 text-xs" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Average Monthly</div>
                  <div className="text-xs font-semibold text-gray-900">
                    ${filteredData.length > 0 ? fmt(avgSpend) : '—'}
                  </div>
                </div>
                <div className="w-10 h-10 bg-indigo-100 rounded flex items-center justify-center">
                  <FontAwesomeIcon icon={faChartLine} className="text-indigo-600 text-xs" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Highest Month</div>
                  {highestMonth ? (
                    <>
                      <div className="text-xs font-semibold text-gray-900">{highestMonth.monthName}</div>
                      <div className="text-[10px] text-gray-600">${fmt(highestMonth.total)}</div>
                    </>
                  ) : (
                    <div className="text-xs text-gray-400">—</div>
                  )}
                </div>
                <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                  <FontAwesomeIcon icon={faArrowUp} className="text-red-600 text-xs" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Lowest Month</div>
                  {lowestMonth ? (
                    <>
                      <div className="text-xs font-semibold text-gray-900">{lowestMonth.monthName}</div>
                      <div className="text-[10px] text-gray-600">${fmt(lowestMonth.total)}</div>
                    </>
                  ) : (
                    <div className="text-xs text-gray-400">—</div>
                  )}
                </div>
                <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                  <FontAwesomeIcon icon={faArrowDown} className="text-green-600 text-xs" />
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Trends Table */}
          <div className="bg-white border border-gray-300 rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Month', 'AWS', 'GCP', 'GitHub', 'Custom', 'Total', 'Bills Uploaded', 'Change'].map(h => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${h === 'Month' ? 'text-left' : 'text-right'}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCalendar} className="text-gray-400 text-xs mr-2" />
                            <span className="text-xs font-medium text-gray-900">
                              {item.monthName} {item.year}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <FontAwesomeIcon icon={getProviderIcon('aws')} className={`${getProviderColor('aws')} text-xs mr-2`} />
                            <span className="text-xs text-gray-900">${fmt(item.aws)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <FontAwesomeIcon icon={getProviderIcon('gcp')} className={`${getProviderColor('gcp')} text-xs mr-2`} />
                            <span className="text-xs text-gray-900">${fmt(item.gcp)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <FontAwesomeIcon icon={getProviderIcon('github')} className={`${getProviderColor('github')} text-xs mr-2`} />
                            <span className="text-xs text-gray-600">${fmt(item.github)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-xs text-gray-900">${fmt(item.custom)}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-xs font-semibold text-gray-900">${fmt(item.total)}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <FontAwesomeIcon icon={faFileInvoice} className="text-indigo-600 text-xs mr-2" />
                            <span className="text-xs font-semibold text-gray-900">${fmt(item.billsUploaded)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end">
                            <FontAwesomeIcon
                              icon={getTrendIcon(item.change)}
                              className={`${getTrendColor(item.change)} text-xs mr-1`}
                            />
                            <span className={`text-xs font-medium ${getTrendColor(item.change)}`}>
                              {item.changePercent > 0 ? '+' : ''}{item.changePercent.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center">
                        <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-300 mb-3" />
                        <div className="text-xs font-medium text-gray-900 mb-1">No Data Available</div>
                        <div className="text-xs text-gray-600">
                          No monthly cost data found for {selectedYear}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Provider Breakdown */}
          {filteredData.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {(['aws', 'gcp', 'custom'] as const).map((provider) => {
                const providerTotal = filteredData.reduce((sum, item) => sum + item[provider], 0);
                const providerAvg = filteredData.length > 0 ? providerTotal / filteredData.length : 0;
                const providerName = provider === 'aws' ? 'AWS' : provider === 'gcp' ? 'GCP' : 'Custom Costs';

                return (
                  <div key={provider} className="bg-white border border-gray-300 rounded p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={getProviderIcon(provider)}
                          className={`${getProviderColor(provider)} text-sm mr-2`}
                        />
                        <span className="text-xs font-semibold text-gray-900">{providerName}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Total</span>
                        <span className="text-xs font-semibold text-gray-900">${fmt(providerTotal)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Average</span>
                        <span className="text-xs font-medium text-gray-900">${fmt(providerAvg)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
